import React from 'react';
import { Badge, Button, Card, EmptyState, Spinner, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';

const TONE = { Pending: 'warning', Accepted: 'success', Revoked: 'neutral', Expired: 'neutral' };

export default function InvitesTab({ invites, loading, canManage, onChanged, onInvite }) {
  const toast = useToast();
  const t = useT();
  const [busyId, setBusyId] = React.useState(null);

  const resend = async (id) => {
    setBusyId(id);
    try {
      const res = await usersApi.resendInvite(id);
      toast.success(t('Invitation resent'));
      if (res?.acceptUrl) {
        try { await navigator.clipboard.writeText(res.acceptUrl); toast.success(t('New invite link copied')); } catch { /* ignore */ }
      }
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not resend'), { description: err?.message });
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (id) => {
    setBusyId(id);
    try {
      await usersApi.revokeInvite(id);
      toast.success(t('Invitation revoked'));
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not revoke'), { description: err?.message });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  if (invites.length === 0) {
    return (
      <EmptyState
        icon={<Icon.Mail size={28} />}
        title={t('No invitations')}
        description={t("Invite teammates to join your organization. They'll set their own password from the emailed link.")}
        action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={15} />} onClick={onInvite}>{t('Invite user')}</Button> : null}
      />
    );
  }

  return (
    <div className="set-prop-list">
      {invites.map((inv) => {
        const pending = inv.status === 'Pending';
        return (
          <Card key={inv.id} variant="flat">
            <Card.Body>
              <div className="set-prop">
                <div className="set-prop__main">
                  <span className="set-prop__name">
                    {inv.email}
                    <Badge tone={TONE[inv.status] || 'neutral'}>{t(inv.status)}</Badge>
                  </span>
                  <span className="usr-email">
                    {t('Expires {date}', { date: new Date(inv.expiresAt).toLocaleDateString() })}
                  </span>
                </div>
                {canManage && pending && (
                  <div className="usr-actions">
                    <Button variant="ghost" size="sm" loading={busyId === inv.id} leadingIcon={<Icon.Refresh size={15} />} onClick={() => resend(inv.id)}>
                      {t('Resend')}
                    </Button>
                    <Button variant="ghost" size="sm" loading={busyId === inv.id} leadingIcon={<Icon.Ban size={15} />} onClick={() => revoke(inv.id)}>
                      {t('Revoke')}
                    </Button>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}
